<template>
    <div ref="datePickerContainer" class="form-field datepicker-container">
        <div class="form-field__input" @click.stop="openCalendar">
            <label
                v-if="label"
                :for="id"
                class="form-field__label"
                :class="{ 'is-active': hasValue || isOpen }"
            >
                {{ label }}
            </label>

            <div
                :class="[
                    'datepicker-field',
                    {
                        'datepicker-field_open': isOpen,
                        'has-value': hasValue,
                    },
                ]"
            >
                <span :class="{ 'has-value': hasValue }">
                    {{ displayValue || placeholder }}
                </span>

                <div
                    v-if="hasValue"
                    class="datepicker-field__clear"
                    @click.stop="clearDate"
                >
                    <img v-svg-inline src="@/assets/icons/ui/field-clear.svg" />
                </div>

                <img
                    v-svg-inline
                    src="@/assets/icons/ui/calendar.svg"
                    class="datepicker-field__icon"
                />
            </div>

            <input
                :id="id"
                ref="dateInput"
                type="text"
                class="datepicker-input"
                readonly
            />
        </div>

        <span v-if="hasError" class="form-field__error">
            {{ errorMessage }}
        </span>
    </div>
</template>

<script lang="ts" setup>
    import 'flatpickr/dist/themes/material_blue.css';

    import {
        ref,
        computed,
        onMounted,
        onBeforeUnmount,
        defineProps,
        defineEmits,
    } from 'vue';
    import flatpickr from 'flatpickr';
    import { Russian } from 'flatpickr/dist/l10n/ru.js';

    const props = defineProps({
        modelValue: {
            type: [String, Array],
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        errorMessage: {
            type: String,
            default: '',
        },
        id: {
            type: String,
            default: '',
        },
        options: {
            type: Object,
            default: () => ({}),
        },
    });

    const emit = defineEmits(['update:modelValue']);

    const isOpen = ref(false);
    const dateInput = ref<HTMLInputElement | null>(null);
    const datePickerContainer = ref<HTMLElement | null>(null);

    // немного переделал метод, т.к теперь range появился и может быть массив дат
    const hasValue = computed(() => {
        if (typeof props.modelValue === 'string') {
            return !!props.modelValue;
        }

        if (Array.isArray(props.modelValue)) {
            return props.modelValue.filter(Boolean).length > 0;
        }

        return false;
    });

    const displayValue = computed(() => {
        if (!hasValue.value) {
            return '';
        }

        if (Array.isArray(props.modelValue)) {
            return props.modelValue.filter(Boolean).join(' - ');
        }

        return props.modelValue;
    });

    const hasError = computed(() => !!props.errorMessage);

    const mergedOptions = computed(() => {
        const base: any = {
            ...props.options,
            locale: Russian,
            disableMobile: true,
        };

        if (!base.dateFormat) {
            base.dateFormat = base.enableTime ? 'd.m.Y H:i' : 'd.m.Y';
        }

        if (base.mode === 'range') {
            if (Array.isArray(props.modelValue) && props.modelValue.length) {
                base.defaultDate = props.modelValue;
            }
            else {
                base.defaultDate = null;
            }
        }
        else {
            base.defaultDate = typeof props.modelValue === 'string' ? props.modelValue : null;
        }

        base.onOpen = (): void => {
            isOpen.value = true;
        };

        base.onClose = (): void => {
            isOpen.value = false;
        };

        base.onChange = (_selectedDates: Date[], dateStr: string): void => {
            if (base.mode === 'range') {
                const formatedDates = dateStr.split('—').map((formatedDate) => formatedDate.trim());
                const rangeDates = [
                    formatedDates.length > 0 ? formatedDates[0] : undefined,
                    formatedDates.length > 1 ? formatedDates[1] : undefined,
                ];

                emit('update:modelValue', rangeDates);
            }
            else {
                emit('update:modelValue', dateStr);
            }
        };

        return base;
    });

    function openCalendar(): void {
        if (dateInput.value) {
            dateInput.value.click();
        }
    }

    function clearDate(): void {
        if (mergedOptions.value.mode === 'range') {
            emit('update:modelValue', []);
        }
        else {
            emit('update:modelValue', '');
        }
    }

    function handleClickOutside(event: MouseEvent): void {
        if (
            datePickerContainer.value &&
            !datePickerContainer.value.contains(event.target as Node)
        ) {
            isOpen.value = false;
        }
    }

    onMounted(() => {

        /* if (dateInput.value) {
            flatpickr(dateInput.value, {
                ...props.options,
                locale: Russian,
                enableTime: true,
                dateFormat: 'd.m.y H:i',
                defaultDate: props.modelValue,
                onOpen() {
                    isOpen.value = true;
                },
                onClose() {
                    isOpen.value = false;
                },
                onChange(selectedDates, dateStr) {
                    emit('update:modelValue', dateStr);
                },
            });
        }*/
        if (dateInput.value) {
            flatpickr(dateInput.value, mergedOptions.value);
        }
        window.addEventListener('click', handleClickOutside);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('click', handleClickOutside);
    });
</script>

<style scoped lang="scss">
    @use './UIDatePicker.scss' as *;
</style>

<style lang="scss">
    @use './UIDatePickerDefault.scss' as *;
</style>
